/**
 * Keyword search for `Keyword mentions & frequency`: what a query matches, and
 * which other keywords are related to those matches. The prototype's
 * `data/keywords.ts`, reading the contract's per-keyword counts and topic
 * split instead of rows — the same arithmetic, so the same ranking.
 */
import type { KeywordFrequency } from '@/data-sources/contract/widgets/trends';

/** How one keyword stands against the current query. */
export type KeywordRelation = 'match' | 'related';

export type KeywordHit = {
  keyword: KeywordFrequency;
  relation: KeywordRelation;
};

/**
 * The share of a topic's mentions a keyword must carry to count as related —
 * 4%, measured against the corpus in the prototype (a keyword its topic names
 * carries 5% or more; one only the generator's floor reaches, about 0.2%).
 */
export const RELATED_SHARE = 0.04;

/** Lower-case and trim, so `  Net ` and `net` are the same query. */
function normalise(query: string): string {
  return query.trim().toLocaleLowerCase();
}

/** Whether a keyword matches the query, in either script (label or gloss), as a substring. */
export function keywordMatches(
  keyword: Pick<KeywordFrequency, 'label' | 'gloss'>,
  query: string,
): boolean {
  const q = normalise(query);
  if (q === '') return true;
  return (
    keyword.label.toLocaleLowerCase().includes(q) ||
    (keyword.gloss ?? '').toLocaleLowerCase().includes(q)
  );
}

/**
 * Every keyword, ranked for the query.
 *
 * - **No query:** every keyword by volume, all `match`.
 * - **A query:** the matches by volume first, then the related keywords by
 *   volume. A query that matches nothing returns nothing.
 *
 * A keyword is related when, weighting its share of each topic by how much of
 * the matches' conversation sits in that topic, the sum reaches `RELATED_SHARE`.
 */
export function searchKeywords(keywords: readonly KeywordFrequency[], query: string): KeywordHit[] {
  const byVolume = (a: KeywordFrequency, b: KeywordFrequency) =>
    b.mentions - a.mentions || a.index - b.index;

  const present = keywords.filter((keyword) => keyword.mentions > 0);
  const matched = present.filter((keyword) => keywordMatches(keyword, query)).sort(byVolume);

  const hit =
    (relation: KeywordRelation) =>
    (keyword: KeywordFrequency): KeywordHit => ({ keyword, relation });

  if (normalise(query) === '' || matched.length === 0) return matched.map(hit('match'));

  /* p(topic | the matches) — where the matched conversation sits. */
  const matchedSet = new Set(matched.map((keyword) => keyword.key));
  const topicOfMatch = new Map<string, number>();
  let matchedTotal = 0;
  /* Each topic's total, for p(keyword | topic). */
  const topicTotal = new Map<string, number>();

  for (const keyword of present) {
    const isMatch = matchedSet.has(keyword.key);
    for (const [topic, count] of Object.entries(keyword.byTopic)) {
      topicTotal.set(topic, (topicTotal.get(topic) ?? 0) + count);
      if (isMatch) {
        topicOfMatch.set(topic, (topicOfMatch.get(topic) ?? 0) + count);
        matchedTotal += count;
      }
    }
  }

  const related = present
    .filter((keyword) => !matchedSet.has(keyword.key))
    .filter((keyword) => {
      let score = 0;
      for (const [topic, count] of topicOfMatch) {
        const total = topicTotal.get(topic) ?? 0;
        if (total === 0) continue;
        score += (count / matchedTotal) * ((keyword.byTopic[topic] ?? 0) / total);
      }
      return score >= RELATED_SHARE;
    })
    .sort(byVolume);

  return [...matched.map(hit('match')), ...related.map(hit('related'))];
}
